/**
 * Rock Off Retail — Personalisation v1 affinity tracker (localStorage scale)
 * + wishlist v1. ROCK-OFF-RETAIL-PLAN.md §E: "Recognition = Klaviyo
 * first-party cookie (__kla_id) ... Flow: localStorage fast path ->
 * __kla_id present? fetch profile affinity via Shopify app proxy -> small
 * cached worker -> else Shopify native recommendations fallback. All
 * browsing dual-writes to localStorage + Klaviyo events/properties (Viewed
 * Hub, Followed Band)."
 *
 * Klaviyo dual-write went LIVE 17 Aug 2026 (public site key XqZ8PG, no app
 * proxy needed — see dualWrite() below and data/klaviyo-event-schema.md).
 *
 * Loaded site-wide, deferred, from layout/theme.liquid — after the other
 * deferred core scripts, well clear of LCP.
 *
 * Consent note (UPDATED 1 Sep 2026): the consent banner SHIPPED —
 * assets/rockoff-consent.js defines window.ROConsent and loads (deferred)
 * before this file in layout/theme.liquid. isConsented() below now fails
 * CLOSED when ROConsent is absent, and gates BOTH the Klaviyo dualWrite and
 * the passive localStorage tracking (recordHubView/recordProductView).
 * Explicit user actions (follow, wishlist) remain functional storage and
 * still work locally pre-consent; their Klaviyo writes stay gated.
 * UPDATED 29 Sep 2026: consent now comes from Shopify's Customer Privacy
 * API via ROConsent; the Klaviyo dualWrite additionally requires
 * ROConsent.allowed('marketing'). The
 * ?demo_affinity= seed is a staff/demo tool: it is ignored on the published
 * theme (Shopify.theme.role === 'main') and only honoured in the theme
 * editor / a preview theme; when honoured it keeps wishlist + follows.
 * Automatic page-view tracking waits for ROConsent.onReady (fix round 1).
 *
 * Public API (window.ROAffinity):
 *   get()                    -> the full affinity record (read-only use)
 *   recordHubView(handle)
 *   recordProductView({id, handle, hub})
 *   isFollowing(handle)      -> boolean
 *   toggleFollow(handle)     -> boolean (the new following state)
 *   isWishlisted(handle)     -> boolean
 *   toggleWishlist({id, handle, hub}) -> boolean (the new wishlisted state)
 *
 * Storage shape (versioned so a future migration can detect and upgrade it —
 * SCHEMA_VERSION stays 1 for this addition: `wishlist` is additive and every
 * reader below defends against it being absent on already-stored v1 records,
 * so existing hubs/products/follows data is never wiped just to gain the
 * new array — see load()):
 *   {
 *     v: 1,
 *     hubs: { <hub_handle>: { count: Number, last: <ms epoch> } },
 *     products: [ { id, handle, hub, ts } ]  // newest first, capped ~24
 *     follows: [ <hub_handle>, ... ]
 *     wishlist: [ { id, handle, hub, ts } ]  // newest first, capped ~48
 *   }
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ro_affinity_v1';
  var SCHEMA_VERSION = 1;
  var PRODUCTS_CAP = 24;
  var WISHLIST_CAP = 48;

  function safeParseJSON(raw) {
    try {
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  }

  function blankAffinity() {
    return { v: SCHEMA_VERSION, hubs: {}, products: [], follows: [], wishlist: [] };
  }

  function load() {
    var raw = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      // Privacy mode / storage disabled — treat as no affinity, never throw.
      return blankAffinity();
    }
    var data = raw ? safeParseJSON(raw) : null;
    if (!data || data.v !== SCHEMA_VERSION) {
      data = blankAffinity();
    }
    data.hubs = data.hubs && typeof data.hubs === 'object' ? data.hubs : {};
    data.products = Array.isArray(data.products) ? data.products : [];
    data.follows = Array.isArray(data.follows) ? data.follows : [];
    // Additive field (17 Aug, wishlist v1) — a record saved before this
    // shipped is still `v: 1` (SCHEMA_VERSION unchanged, see file header
    // comment) and simply lacks `wishlist`; default it in rather than
    // treating the whole record as stale, so an existing visitor's
    // hubs/products/follows survive untouched the first time this runs.
    data.wishlist = Array.isArray(data.wishlist) ? data.wishlist : [];
    return data;
  }

  function save(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      // Quota exceeded / privacy mode — personalisation degrades silently,
      // never breaks the page.
    }
  }

  // -------------------------------------------------------------------
  // Dual-write seam (plan §E). LIVE as of 17 Aug 2026 — Klaviyo account
  // exists (public site key XqZ8PG). Wired per data/klaviyo-event-schema.md:
  //   1. Read the `__kla_id` cookie Klaviyo's onsite JS sets once installed.
  //   2. POST straight to Klaviyo's public client Events/Profiles endpoints
  //      (https://a.klaviyo.com/client/events|profiles/?company_id=XqZ8PG) —
  //      no app proxy / server-side worker needed for v1 (that "no public
  //      write key exists" caveat predates Klaviyo's public client-events
  //      endpoint — see schema doc).
  //   3. Public key only, ever — the private key must never appear in this
  //      file or anywhere under theme/.
  // Named + called from every write path below so this seam never required
  // touching recordHubView/recordProductView/toggleFollow/toggleWishlist —
  // see data/klaviyo-event-schema.md for the exact payload shapes.
  // -------------------------------------------------------------------
  var KLAVIYO_PUBLIC_KEY = 'XqZ8PG';
  var KLAVIYO_API_REVISION = '2026-07-15';
  var KLAVIYO_EVENTS_URL = 'https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_PUBLIC_KEY;
  var KLAVIYO_PROFILES_URL = 'https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_PUBLIC_KEY;
  var KLA_QUEUE_KEY = 'ro_kla_queue_v1';
  var KLA_QUEUE_CAP = 50;
  // In-memory only (resets on page reload — a redundant top_hub PATCH once
  // per fresh page load is harmless, just avoids one on every hub view
  // within the same load).
  var lastDualWrittenTopHub = null;

  // Consent gate — ROConsent SHIPPED 1 Sep 2026 (assets/rockoff-consent.js,
  // loaded deferred BEFORE this file in layout/theme.liquid, so it exists by
  // the time anything here runs). Fails CLOSED now: if the consent script is
  // missing/broken, no tracking happens (UK PECR posture — the pre-banner
  // "defaults open" fallback is retired). Gates both the Klaviyo dualWrite
  // AND the passive localStorage tracking below (recordHubView /
  // recordProductView); explicit user actions (follow, wishlist) stay
  // functional locally, with their Klaviyo writes still consent-gated here.
  function isConsented() {
    if (window.ROConsent && typeof window.ROConsent.allowed === 'function') {
      try {
        return !!window.ROConsent.allowed('personalisation');
      } catch (err) {
        return false;
      }
    }
    return false;
  }

  // Klaviyo sends need MARKETING consent on top of personalisation (Shopify
  // Customer Privacy API, read through ROConsent since 29 Sep 2026). Fails
  // CLOSED the same way: no ROConsent, or any throw -> false.
  function isMarketingConsented() {
    if (window.ROConsent && typeof window.ROConsent.allowed === 'function') {
      try {
        return !!window.ROConsent.allowed('marketing');
      } catch (err) {
        return false;
      }
    }
    return false;
  }

  // __kla_id cookie read. Klaviyo's onsite JS sets this once installed, or
  // it appears after the visitor clicks a Klaviyo email link / submits a
  // Klaviyo form. No self-generated fallback ID — see klaviyo-event-schema.md
  // "Identity".
  function readKlaId() {
    try {
      var match = document.cookie.match(/(?:^|; )__kla_id=([^;]*)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (err) {
      return null;
    }
  }

  function kUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // Minimal RFC4122-ish fallback for older browsers — fine for a dedupe
    // key, not used for anything security-sensitive.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // Local overflow queue — used only when __kla_id is absent. Flushed the
  // next time dualWrite() runs and finds __kla_id present.
  function loadKlaQueue() {
    try {
      var raw = window.localStorage.getItem(KLA_QUEUE_KEY);
      var data = raw ? JSON.parse(raw) : null;
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return [];
    }
  }

  function saveKlaQueue(queue) {
    try {
      window.localStorage.setItem(KLA_QUEUE_KEY, JSON.stringify(queue));
    } catch (err) {
      // Quota exceeded / privacy mode — degrade silently, same rule as the
      // affinity store itself.
    }
  }

  function enqueueKla(kind, klaviyoPayload) {
    var queue = loadKlaQueue();
    queue.push({ kind: kind, payload: klaviyoPayload, ts: Date.now() });
    if (queue.length > KLA_QUEUE_CAP) {
      queue = queue.slice(queue.length - KLA_QUEUE_CAP);
    }
    saveKlaQueue(queue);
  }

  function sendToKlaviyo(kind, payload) {
    var url = kind === 'profile' ? KLAVIYO_PROFILES_URL : KLAVIYO_EVENTS_URL;
    try {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Revision: KLAVIYO_API_REVISION,
        },
        body: JSON.stringify(payload),
        // keepalive lets the request survive a page unload right after a
        // click (e.g. follow-band then immediate navigation).
        keepalive: true,
      }).catch(function () {
        // Network/CORS/ad-blocker failure — never surface to the page.
      });
    } catch (err) {
      // Fails silently, per contract.
    }
  }

  function flushKlaQueue(klaId) {
    var queue = loadKlaQueue();
    if (!queue.length) return;
    queue.forEach(function (item) {
      stampIdentity(item.payload, klaId);
      sendToKlaviyo(item.kind, item.payload);
    });
    saveKlaQueue([]);
  }

  // Stamps the resolved __kla_id onto a payload's profile.attributes._kx
  // (event payloads) or attributes._kx (profile payloads) right before
  // sending — queued items are built without an identity and get it filled
  // in at flush time.
  function stampIdentity(payload, klaId) {
    if (payload && payload.data && payload.data.attributes) {
      if (payload.data.type === 'event' && payload.data.attributes.profile) {
        payload.data.attributes.profile.data.attributes._kx = klaId;
      } else if (payload.data.type === 'profile') {
        payload.data.attributes._kx = klaId;
      }
    }
    return payload;
  }

  // -------------------------------------------------------------------
  // Hub -> {name, hub_type, genre[], range} lookup. Built from
  // assets/hub-genre-map.json (scripts/build-hub-genre-asset.js, seeded from
  // data/hub-genre-map.json) — fetched once, async, right after this file
  // starts executing (see loadHubMeta() below), cached on window.ROHubMeta.
  // getHubMeta() returns null only for a handle NOT PRESENT in the map at
  // all (a hub build-hub-genre-asset.js has no coverage for — character
  // hubs, or a DRAFT hub excluded by its ACTIVE-only filter — or, before
  // the fetch below resolves, EVERY handle) — every call site treats that
  // null as "omit the property", never throws on it, so a hub view fired
  // by autoTrack() on first paint, before the asset has loaded, ships
  // without Genre/Range/HubName rather than blocking on the network,
  // matching the "fails silently, never blocks a UI interaction" contract.
  // The 10 uncategorised interest hubs (data/hub-genre-map.json `gaps`) are
  // NOT in that "returns null" case — they ARE in the map, with an explicit
  // `range: null`, so getHubMeta() returns a real object for them and
  // applyHubContext() sends `Range: null` on the event (the schema's
  // deliberate "gap is visible" distinction — see that function's comment).
  // -------------------------------------------------------------------
  function getHubMeta(handle) {
    if (!handle) return null;
    var map = window.ROHubMeta || {};
    return map[handle] || null;
  }

  // Resolved relative to this script's own URL so no Liquid/theme.liquid
  // change is needed to pass the asset URL in — document.currentScript is
  // only valid synchronously while this file is first executing, so it's
  // captured up front, before any async work.
  function loadHubMeta() {
    var src = document.currentScript && document.currentScript.src;
    if (!src) return;
    var url = src.replace(/rockoff-affinity\.js(\?.*)?$/, 'hub-genre-map.json');
    if (url === src) return; // regex didn't match — bail rather than fetch a wrong URL
    fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (map) {
        if (map && typeof map === 'object') window.ROHubMeta = map;
      })
      .catch(function () {
        // Leaves window.ROHubMeta unset — getHubMeta() already handles that.
      });
  }
  loadHubMeta();

  // Given the current affinity record (this file's own `load()` return
  // value — same shape, `data.hubs[handle] = {count, last}`), picks the hub
  // with the highest view count, ties broken by most recent `last`. Returns
  // null if there's no hub activity yet (fresh visitor — stays at tier 3,
  // store top sellers, until they view or follow something).
  function computeTopHubFromAffinity(affinityData) {
    var hubs = (affinityData && affinityData.hubs) || {};
    var bestHandle = null;
    var best = null;
    Object.keys(hubs).forEach(function (handle) {
      var entry = hubs[handle];
      if (!best || entry.count > best.count || (entry.count === best.count && entry.last > best.last)) {
        best = entry;
        bestHandle = handle;
      }
    });
    return bestHandle;
  }

  // Adds HubHandle/HubName/HubType + Genre (band hubs) or Range (interest
  // hubs, including explicit `Range: null` for the flagged uncategorised
  // ones — see klaviyo-event-schema.md "Events") onto an event properties
  // object. Mutates and returns `properties` for convenience.
  //
  // 18 Aug 2026: hub-genre-map.json gained a 3rd interest shape —
  // `character_type: true` (film/tv/gaming, the Character mega's "By Type"
  // destinations — see scripts/build-hub-genre-asset.js header + gaming_hub_
  // note in data/hub-genre-map.json). These are `hub_type: "interest"` but
  // carry NO `range` key at all, so `meta.range !== undefined ? ... : null`
  // would silently send `Range: null` — indistinguishable from the schema's
  // deliberate "known hub, genuinely uncategorised range" gap signal
  // (klaviyo-event-schema.md "Events"). Minimal fix: recognise the flag and
  // omit Range entirely for these 3 handles instead of sending a misleading
  // null. A dedicated Klaviyo property for "this is a type destination, not
  // a range" is a schema-doc decision, out of scope for this pass.
  function applyHubContext(properties, handle) {
    if (!handle) return properties;
    properties.HubHandle = handle;
    var meta = getHubMeta(handle);
    if (!meta) return properties; // gap in hub-genre-map.json — omit context, don't guess
    properties.HubName = meta.name;
    properties.HubType = meta.hub_type;
    if (meta.hub_type === 'band') {
      properties.Genre = meta.genre || [];
    } else if (meta.hub_type === 'interest' && !meta.character_type) {
      properties.Range = meta.range !== undefined ? meta.range : null;
    }
    return properties;
  }

  // -------------------------------------------------------------------
  // Payload builders — shapes match data/klaviyo-event-schema.md exactly.
  // -------------------------------------------------------------------
  function buildEventPayload(metricName, properties) {
    return {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric: { data: { type: 'metric', attributes: { name: metricName } } },
          profile: { data: { type: 'profile', attributes: {} } }, // _kx stamped just before send
          unique_id: kUuid(),
        },
      },
    };
  }

  function buildProfilePatchPayload(patchOp, bandHandle) {
    var patch = {};
    patch[patchOp] = { followed_bands: bandHandle };
    return {
      data: {
        type: 'profile',
        attributes: { properties: {} }, // _kx stamped just before send
        meta: { patch_properties: patch },
      },
    };
  }

  // top_hub/top_genre are plain overwrites, not array patches — see
  // klaviyo-event-schema.md "(c) the top_hub / top_genre derived affinity
  // properties".
  function buildTopAffinityPatchPayload(topHubHandle) {
    var meta = getHubMeta(topHubHandle);
    var props = { top_hub: topHubHandle };
    // 18 Aug: same character_type carve-out as applyHubContext() above —
    // a Film/TV/Gaming top hub has no genre/range concept, so leave
    // top_genre unset rather than sending a misleading null.
    if (meta && !meta.character_type) {
      props.top_genre = meta.hub_type === 'band' ? (meta.genre && meta.genre[0]) || null : meta.range || null;
    }
    return {
      data: {
        type: 'profile',
        attributes: { properties: props }, // _kx stamped just before send
      },
    };
  }

  // -------------------------------------------------------------------

  // -------------------------------------------------------------------
  // Personalisation v2 server feed (15 Sep 2026, build prompt step 4a/4c).
  // Every EVENT payload dualWrite() sends to Klaviyo also goes, as a
  // fire-and-forget beacon, to the worker's POST /events, so storefront
  // behaviour, email behaviour and orders meet in one table
  // (personalisation-worker/migrations/003-visitor-events.sql). Consent-
  // gated exactly like the Klaviyo call. Identity = the ro_vid first-party
  // cookie (minted here, only after consent, 1 year) plus __kla_id when
  // present. Profile PATCHes are NOT mirrored: the worker rebuilds affinity
  // from events itself. Beacons are text/plain because sendBeacon cannot
  // preflight; the worker parses the JSON. Never throws, never blocks UI.
  // -------------------------------------------------------------------
  var RO_WORKER_URL = 'https://rockoff-personalisation-1270ee454e5a.herokuapp.com';
  var RO_VID_COOKIE = 'ro_vid';
  var beaconQueue = [];
  var beaconTimer = null;

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (err) {
      return null;
    }
  }

  function getOrMintVid() {
    if (!isConsented()) return null;
    var v = readCookie(RO_VID_COOKIE);
    if (v && /^[A-Za-z0-9-]{8,64}$/.test(v)) return v;
    v = kUuid();
    try {
      document.cookie = RO_VID_COOKIE + '=' + v + '; Max-Age=31536000; Path=/; SameSite=Lax; Secure';
    } catch (err) {
      /* cookie blocked: the id lives for this page only */
    }
    return v;
  }

  function postBeacon(path, body) {
    var json = JSON.stringify(body);
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(RO_WORKER_URL + path, new Blob([json], { type: 'text/plain' }))) {
        return;
      }
    } catch (err) {
      /* fall through to fetch */
    }
    try {
      fetch(RO_WORKER_URL + path, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: json,
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
      }).catch(function () {});
    } catch (err) {
      /* fails silently, per contract */
    }
  }

  function flushBeacons() {
    beaconTimer = null;
    if (!beaconQueue.length) return;
    var vid = getOrMintVid();
    if (!vid) {
      beaconQueue = [];
      return;
    }
    var klaId = readKlaId();
    // Send every queued batch now (a pagehide flush cannot wait for a
    // timer): 25 events per beacon, looped.
    while (beaconQueue.length) {
      var batch = beaconQueue.splice(0, 25);
      postBeacon('/events', { v: 1, vid: vid, kla_id: klaId, events: batch });
    }
  }

  function sendEventBeacon(metricName, properties) {
    if (!isConsented()) return;
    beaconQueue.push({ name: metricName, properties: properties || {}, ts: Date.now(), uid: kUuid() });
    if (!beaconTimer) beaconTimer = setTimeout(flushBeacons, 400); // micro-batch
  }

  // Email stitch (step 4c): the worker hashes the address (sha256, lower-
  // cased) and links it to ro_vid / __kla_id. The raw email is not stored.
  // Returns true when a beacon was actually posted. customer_id (logged-in
  // meta, when present) lets the worker verify the email against the Shopify
  // customer record; without it the link is recorded as unverified.
  function identify(email) {
    if (!isConsented() || typeof email !== 'string' || email.indexOf('@') === -1) return false;
    var vid = getOrMintVid();
    if (!vid) return false;
    var idMeta = document.querySelector('meta[name="ro-customer-id"]');
    var body = { ro_vid: vid, kla_id: readKlaId(), email: email.trim() };
    if (idMeta && idMeta.getAttribute('content')) body.customer_id = idMeta.getAttribute('content');
    postBeacon('/identify', body);
    return true;
  }

  function bindServerFeed() {
    // Never feed events from the theme editor preview.
    if (window.Shopify && window.Shopify.designMode) return;
    // Added to Cart: only Dawn's product-form.js (PDP + quick-add modal)
    // publishes cartUpdate with the /cart/add.js line item (source
    // 'product-form'). cart.js ('cart-items') and quick-add-bulk
    // ('quick-add') publish the WHOLE cart, where items[0] is not the changed
    // product, so those are ignored (16 Sep 2026 check, finding 3).
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
          try {
            if (!evt || evt.source !== 'product-form') return;
            var data = evt.cartData;
            var item = data && data.handle ? data : data && data.items && data.items[0];
            if (!item || !item.handle) return;
            sendEventBeacon('Added to Cart', {
              ProductID: item.product_id || null,
              ProductHandle: item.handle,
              ProductName: item.product_title || null,
              VariantID: item.variant_id || item.id || null,
              Quantity: item.quantity || 1,
            });
          } catch (err) {
            /* never surface */
          }
        });
      }
    } catch (err) {
      /* pubsub missing: no cart events, nothing else affected */
    }

    // Logged-in customer: layout/theme.liquid renders the meta tags.
    var meta = document.querySelector('meta[name="ro-customer-email"]');
    var email = meta && meta.getAttribute('content');
    if (email) {
      var done = false;
      try {
        done = sessionStorage.getItem('ro_identified_v1') === '1';
      } catch (err) {
        done = false;
      }
      if (!done && identify(email)) {
        try {
          sessionStorage.setItem('ro_identified_v1', '1');
        } catch (err) {
          /* ignore */
        }
      }
    }
    // Newsletter / contact forms (Dawn: input[name="contact[email]"]).
    document.addEventListener(
      'submit',
      function (e) {
        try {
          var form = e.target;
          if (!form || !form.querySelector) return;
          var input = form.querySelector('input[name="contact[email]"], input[type="email"]');
          if (input && input.value) identify(input.value);
        } catch (err) {
          /* ignore */
        }
      },
      true
    );
    // Klaviyo onsite forms.
    window.addEventListener('klaviyoForms', function (e) {
      try {
        var d = e && e.detail;
        if (d && d.type === 'submit' && d.metaData && d.metaData.$email) identify(d.metaData.$email);
      } catch (err) {
        /* ignore */
      }
    });
  }

  // dualWrite() — the seam. Same signature as before:
  //   dualWrite(event, payload)
  // event is one of: 'hub_view' | 'product_view' | 'follow_toggle' |
  // 'wishlist_add' (wishlist v1 — see toggleWishlist() below; only fires on
  // add, per data/klaviyo-event-schema.md "Added to Wishlist").
  // -------------------------------------------------------------------
  function dualWrite(event, payload) {
    // Personalisation alone is not enough to send events to Klaviyo.
    if (!isConsented() || !isMarketingConsented()) return;

    var klaId = readKlaId();
    var toSend = [];

    if (event === 'hub_view') {
      var hubProps = applyHubContext({}, payload.handle);
      toSend.push({ kind: 'event', payload: buildEventPayload('Viewed Hub', hubProps) });

      // Tier 1/2 affinity: recompute top_hub from the just-updated local
      // view-count record and dual-write only when it actually changed
      // (avoids a profile PATCH on every single hub view).
      var affinity = load(); // same load() this file already defines
      var newTopHub = computeTopHubFromAffinity(affinity);
      if (newTopHub && newTopHub !== lastDualWrittenTopHub) {
        toSend.push({ kind: 'profile', payload: buildTopAffinityPatchPayload(newTopHub) });
        lastDualWrittenTopHub = newTopHub;
      }
    } else if (event === 'product_view') {
      var productProps = {
        ProductID: payload.id || null,
        ProductHandle: payload.handle,
      };
      applyHubContext(productProps, payload.hub);
      toSend.push({ kind: 'event', payload: buildEventPayload('Viewed Product', productProps) });
    } else if (event === 'follow_toggle') {
      var metricName = payload.following ? 'Followed Band' : 'Unfollowed Band';
      toSend.push({ kind: 'event', payload: buildEventPayload(metricName, applyHubContext({}, payload.handle)) });
      var patchOp = payload.following ? 'append' : 'remove';
      toSend.push({ kind: 'profile', payload: buildProfilePatchPayload(patchOp, payload.handle) });

      // A follow is an explicit, stronger signal than a view count — it wins
      // top_hub outright regardless of what the view-count tally says.
      if (payload.following) {
        toSend.push({ kind: 'profile', payload: buildTopAffinityPatchPayload(payload.handle) });
        lastDualWrittenTopHub = payload.handle;
      }
    } else if (event === 'wishlist_add') {
      var wishlistProps = {
        ProductID: payload.id || null,
        ProductHandle: payload.handle,
      };
      applyHubContext(wishlistProps, payload.hub);
      toSend.push({ kind: 'event', payload: buildEventPayload('Added to Wishlist', wishlistProps) });
    } else {
      return; // Unknown event name — never throw, just skip.
    }

    // v2 server feed: mirror every EVENT payload (never the profile
    // PATCHes) to the worker, same consent gate, same properties.
    toSend.forEach(function (item) {
      if (item.kind !== 'event') return;
      try {
        var attrs = item.payload.data.attributes;
        sendEventBeacon(attrs.metric.data.attributes.name, attrs.properties);
      } catch (err) {
        /* never block the Klaviyo path */
      }
    });

    if (klaId) {
      // Identity known: flush anything queued from earlier in the session
      // first (in order), then send this call's payload(s).
      flushKlaQueue(klaId);
      toSend.forEach(function (item) {
        stampIdentity(item.payload, klaId);
        sendToKlaviyo(item.kind, item.payload);
      });
    } else {
      // No identity yet: queue, don't send. Flushed by a later dualWrite()
      // call once __kla_id appears (e.g. after a Klaviyo email click).
      toSend.forEach(function (item) {
        enqueueKla(item.kind, item.payload);
      });
    }
  }

  function recordHubView(handle) {
    if (!handle) return;
    // Passive tracking: consent-gated at the WRITE, not just the dualWrite —
    // no view-history localStorage accrues before Accept (PECR, 1 Sep 2026).
    if (!isConsented()) return;
    var data = load();
    var entry = data.hubs[handle] || { count: 0, last: 0 };
    entry.count += 1;
    entry.last = Date.now();
    data.hubs[handle] = entry;
    save(data);
    dualWrite('hub_view', { handle: handle });
  }

  function recordProductView(product) {
    if (!product || !product.handle) return;
    // Same consent gate as recordHubView — passive tracking only after Accept.
    if (!isConsented()) return;
    var data = load();
    data.products = data.products.filter(function (p) {
      return p.handle !== product.handle;
    });
    data.products.unshift({
      id: product.id || null,
      handle: product.handle,
      hub: product.hub || null,
      ts: Date.now(),
    });
    if (data.products.length > PRODUCTS_CAP) {
      data.products.length = PRODUCTS_CAP;
    }
    save(data);
    dualWrite('product_view', product);
  }

  function isFollowing(handle) {
    if (!handle) return false;
    var data = load();
    return data.follows.indexOf(handle) !== -1;
  }

  function toggleFollow(handle) {
    if (!handle) return false;
    var data = load();
    var idx = data.follows.indexOf(handle);
    var nowFollowing;
    if (idx === -1) {
      data.follows.push(handle);
      nowFollowing = true;
    } else {
      data.follows.splice(idx, 1);
      nowFollowing = false;
    }
    save(data);
    dualWrite('follow_toggle', { handle: handle, following: nowFollowing });
    return nowFollowing;
  }

  // -------------------------------------------------------------------
  // Wishlist v1. Keyed by product handle (matches the id the theme already
  // treats as canonical for a product — see recordProductView). `product`
  // accepts the same shape as recordProductView: {id, handle, hub}, hub
  // optional/nullable (Spiral catalogue items with no band tie).
  // -------------------------------------------------------------------
  function isWishlisted(handle) {
    if (!handle) return false;
    var data = load();
    return data.wishlist.some(function (entry) {
      return entry.handle === handle;
    });
  }

  function toggleWishlist(product) {
    var handle = product && product.handle;
    if (!handle) return false;
    var data = load();
    var idx = data.wishlist.findIndex(function (entry) {
      return entry.handle === handle;
    });
    var nowWishlisted;
    if (idx === -1) {
      data.wishlist.unshift({
        id: product.id || null,
        handle: handle,
        hub: product.hub || null,
        ts: Date.now(),
      });
      if (data.wishlist.length > WISHLIST_CAP) {
        data.wishlist.length = WISHLIST_CAP;
      }
      nowWishlisted = true;
    } else {
      data.wishlist.splice(idx, 1);
      nowWishlisted = false;
    }
    save(data);
    // Klaviyo event fires on ADD only — see data/klaviyo-event-schema.md
    // "Added to Wishlist" (removals are a local-only state change, no event).
    if (nowWishlisted) {
      dualWrite('wishlist_add', { id: product.id || null, handle: handle, hub: product.hub || null });
    }
    return nowWishlisted;
  }

  // -------------------------------------------------------------------
  // Demo mode: ?demo_affinity=iron-maiden,metallica seeds localStorage with
  // synthetic hub views (first handle also gets a synthetic follow) for a
  // fresh KDS-demo browser, then reloads with the param stripped so what
  // gets demoed is the ordinary "recognised visitor" state, not a URL with
  // demo params visibly in the bar. See KDS-DEMO-SCRIPT.md.
  //
  // BUGFIX (18 Aug 2026, Sohail-reported): "unfollowed ALL bands, the rail
  // still shows only Iron Maiden." Root-cause trace (3 questions asked in
  // the brief, answered here):
  //   (a) YES, real bug, this is the primary cause — this function used to
  //       unconditionally re-seed (fully OVERWRITE via blankAffinity())
  //       localStorage every single time the param was present in the URL.
  //       The KDS demo workflow's own documented pattern is a BOOKMARKED
  //       link carrying ?demo_affinity=iron-maiden,metallica — so every
  //       repeat visit to that exact bookmark silently wiped out any real
  //       interaction since the last seed (an explicit unfollow included)
  //       and put Iron Maiden straight back as a synthetic follow. Fixed
  //       below: the exact seed STRING already applied is remembered
  //       (ALREADY_SEEDED_KEY); a repeat load of the same demo link no
  //       longer touches the affinity record at all, so a real unfollow
  //       now actually sticks across reloads of that bookmark. A visit
  //       with a genuinely different ?demo_affinity= value still seeds
  //       fresh, same as before.
  //   (b) NO — toggleFollow() (below) is correct: it does splice the
  //       handle out of follows[] and save() the result. Traced and
  //       confirmed, not the cause.
  //   (c) NOT a stale-cache bug in the sense implied — window.ROAffinity.get()
  //       is exactly load(), which reads localStorage fresh on every call,
  //       and assets/rockoff-for-you.js calls it fresh on every full page
  //       load (this is a classic multi-page storefront, not an SPA — see
  //       that file's initSection(), which now carries an explicit comment
  //       confirming this). `lastDualWrittenTopHub` is a real in-memory
  //       variable, but it only exists to dedupe Klaviyo profile PATCH
  //       calls within one page load — it plays no part in what the rail
  //       renders. What DOES persist across an unfollow, by design, is the
  //       hub's separate VIEW-COUNT record in `hubs{}` (recordHubView) —
  //       unfollowing only ever touches `follows[]`. So once (a) stops
  //       forcing Iron Maiden back into `follows[]` on every reload, a hub
  //       Sohail has actually viewed can still surface via the rail's
  //       documented viewed-hub fallback tier (assets/rockoff-for-you.js
  //       rankHubs() — "followed first, then view count/recency", exactly
  //       what the blend spec asks for) — that is intended ranking
  //       behaviour, not a second bug, but flagged here in case Sohail
  //       wants an unfollow to also clear that hub's view history so it
  //       stops surfacing sooner (not done — open judgement call, not
  //       asked for in the brief).
  // -------------------------------------------------------------------
  var DEMO_SEEDED_KEY = 'ro_demo_seeded_v1';

  // The demo seed is a staff tool and writes passive data without consent, so
  // it is honoured ONLY off the published theme: theme editor (designMode) or
  // a preview / unpublished theme (Shopify.theme.role !== 'main'). On the live
  // storefront the param is ignored entirely. Unknown role -> not allowed.
  function isDemoSeedAllowed() {
    try {
      var s = window.Shopify;
      if (!s) return false;
      if (s.designMode === true) return true;
      return !!(s.theme && s.theme.role && s.theme.role !== 'main');
    } catch (err) {
      return false;
    }
  }

  function seedFromUrl() {
    if (!isDemoSeedAllowed()) return false;
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (err) {
      return false;
    }
    var seed = params.get('demo_affinity');
    if (!seed) return false; // param genuinely absent — never fires

    var handles = seed
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
    if (!handles.length) return false;

    var alreadySeeded = null;
    try {
      alreadySeeded = window.localStorage.getItem(DEMO_SEEDED_KEY);
    } catch (err) {
      // Privacy mode — can't remember a prior seed either way; fall through
      // and treat this as a fresh seed (matches pre-fix behaviour when
      // storage is unavailable, no worse than before).
    }

    var stripParamAndReload = function () {
      params.delete('demo_affinity');
      var qs = params.toString();
      var cleanUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      var alreadyClean = cleanUrl === window.location.pathname + window.location.search + window.location.hash;
      if (alreadyClean) return false; // param wasn't actually in the URL to strip (shouldn't happen, guards a loop)
      window.location.replace(cleanUrl);
      return true;
    };

    if (alreadySeeded === seed) {
      // Same demo link, already applied once — strip the param for a clean
      // address bar (matches prior UX) but do NOT touch real affinity data.
      return stripParamAndReload();
    }

    // Seed hubs/products only; the visitor's existing wishlist and followed
    // bands (explicit, user-created state) are preserved, never overwritten.
    var data = load();
    data.hubs = {};
    data.products = [];
    handles.forEach(function (handle, i) {
      data.hubs[handle] = {
        count: Math.max(3 - i, 1),
        last: Date.now() - i * 60000,
      };
      if (i === 0 && data.follows.indexOf(handle) === -1) data.follows.push(handle);
    });
    save(data);
    try {
      window.localStorage.setItem(DEMO_SEEDED_KEY, seed);
      // Personalisation v2: a fresh seed must REPLACE the server-side record
      // too, or earlier demo bands come back through returning-visitor
      // recognition. rockoff-for-you.js sends reset=1 once and clears this.
      window.localStorage.setItem('ro_affinity_reset_v1', '1');
    } catch (err) {
      // Quota/privacy mode — seed still applied for this load, just can't
      // remember it for next time; degrades to pre-fix (reseed-every-visit)
      // behaviour rather than throwing.
    }
    // Debuggability (18 Aug ask): make it obvious in devtools exactly when
    // and what a demo seed applied, since it's otherwise a silent
    // localStorage write followed by an invisible reload.
    console.info('[ROAffinity] demo_affinity seed applied:', handles.join(', '));

    return stripParamAndReload();
  }

  // If a demo seed just ran, a navigation is already in flight — skip the
  // rest of this load (the reloaded page re-runs this file cleanly).
  if (seedFromUrl()) {
    window.ROAffinity = {
      get: load,
      recordHubView: function () {},
      recordProductView: function () {},
      isFollowing: isFollowing,
      toggleFollow: function () {
        return false;
      },
      isWishlisted: isWishlisted,
      toggleWishlist: function () {
        return false;
      },
    };
    return;
  }

  // -------------------------------------------------------------------
  // Auto-track: hub pages + PDP render a small data-attribute hook (see
  // sections/rockoff-split-banner.liquid and sections/main-product.liquid)
  // rather than duplicating the rockoff.hub metafield resolution in JS.
  // -------------------------------------------------------------------
  function autoTrack() {
    var hubEl = document.querySelector('[data-ro-hub-handle]');
    if (hubEl) {
      var handle = hubEl.getAttribute('data-ro-hub-handle');
      if (handle) recordHubView(handle);
    }

    var pdpEl = document.querySelector('[data-ro-pdp-product]');
    if (pdpEl) {
      recordProductView({
        id: pdpEl.getAttribute('data-ro-pdp-product'),
        handle: pdpEl.getAttribute('data-ro-pdp-handle'),
        hub: pdpEl.getAttribute('data-ro-pdp-hub') || null,
      });
    }
  }

  // -------------------------------------------------------------------
  // Follow button — event-delegated so any number of
  // snippets/rockoff-follow-button.liquid instances on a page (hub hero,
  // PDP band chip) work with zero per-instance JS.
  // -------------------------------------------------------------------
  function bindFollowButtons() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-ro-follow-btn]');
      if (!btn) return;

      var handle = btn.getAttribute('data-ro-follow-handle');
      var name = btn.getAttribute('data-ro-follow-name') || '';
      var following = toggleFollow(handle);

      btn.classList.toggle('is-following', following);
      btn.setAttribute('aria-pressed', following ? 'true' : 'false');

      var labelEl = btn.querySelector('.ro-follow-btn__label');
      if (labelEl) {
        labelEl.textContent = following ? 'FOLLOWING ✓' : '+ FOLLOW ' + name.toUpperCase();
      }
    });
  }

  // -------------------------------------------------------------------
  // Wishlist heart button — event-delegated so any number of
  // snippets/rockoff-wishlist-button.liquid instances on a page (product
  // card grid, PDP buy box) work with zero per-instance JS. Reads
  // data-ro-wishlist-* attributes for {id, handle, hub}, same shape
  // toggleWishlist() expects.
  // -------------------------------------------------------------------
  function bindWishlistButtons() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-ro-wishlist-btn]');
      if (!btn) return;
      evt.preventDefault(); // these buttons live inside <a> product-card links in some placements

      var handle = btn.getAttribute('data-ro-wishlist-handle');
      var wishlisted = toggleWishlist({
        id: btn.getAttribute('data-ro-wishlist-id'),
        handle: handle,
        hub: btn.getAttribute('data-ro-wishlist-hub') || null,
      });

      btn.classList.toggle('is-wishlisted', wishlisted);
      btn.setAttribute('aria-pressed', wishlisted ? 'true' : 'false');
      // Localised copy comes from the button's own data attributes — set by
      // Liquid on render (snippets/rockoff-wishlist-button.liquid, from
      // locales/en.default.json `rockoff.wishlist.add`/`.remove`) for
      // card/PDP hearts, and by assets/rockoff-wishlist.js (reading the
      // same translated strings off the wishlist page's own data
      // attributes) for JS-built /pages/wishlist cards — this function
      // never hardcodes the English phrase itself, just falls back to it
      // defensively if a button is somehow missing those attributes.
      var label = btn.getAttribute('data-ro-wishlist-label') || 'Wishlist';
      var addLabel = btn.getAttribute('data-ro-wishlist-add-label') || 'Add to wishlist';
      var removeLabel = btn.getAttribute('data-ro-wishlist-remove-label') || 'Remove from wishlist';
      btn.setAttribute('aria-label', (wishlisted ? removeLabel : addLabel) + ' ' + label);
    });
  }

  // Automatic page-view tracking waits for ROConsent.onReady (the Shopify
  // Customer Privacy API loads asynchronously; deciding at init would drop
  // the view for every returning, already-consented visitor). Runs at most
  // once per page load: on ready, or - for a visitor who accepts on this very
  // page - on the first consent change. isConsented() is re-checked at the
  // moment of tracking. Fails CLOSED if ROConsent (or onReady) is absent.
  var autoTracked = false;
  function trackWhenConsented() {
    if (autoTracked) return;
    if (!isConsented()) return;
    autoTracked = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', autoTrack);
    } else {
      autoTrack();
    }
  }
  try {
    if (window.ROConsent && typeof window.ROConsent.onReady === 'function') {
      window.ROConsent.onReady(trackWhenConsented);
      if (typeof window.ROConsent.onChange === 'function') window.ROConsent.onChange(trackWhenConsented);
    }
  } catch (err) {
    /* fail closed: no tracking */
  }
  bindFollowButtons();
  bindWishlistButtons();
  bindServerFeed();
  window.addEventListener('pagehide', function () {
    if (beaconQueue.length) {
      clearTimeout(beaconTimer);
      flushBeacons();
    }
  });

  window.ROAffinity = {
    get: load,
    recordHubView: recordHubView,
    recordProductView: recordProductView,
    isFollowing: isFollowing,
    toggleFollow: toggleFollow,
    isWishlisted: isWishlisted,
    toggleWishlist: toggleWishlist,
    // Personalisation v2 (15 Sep 2026): read by rockoff-for-you.js.
    workerUrl: RO_WORKER_URL,
    getVid: getOrMintVid,
    identify: identify,
    sendEvent: sendEventBeacon,
  };
})();
